/**
 * Element utilities
 * =================
 * Index
 *
 * - booleanAttributes -- Array (not exported)
 * - createElement(tagName, attributes)
 * - detectOverflow(element)
 * - focusableElements -- Array
 * - getAttributes(element)
 * - getPosition(element)
 * - isElement(element)
 * - swapTag(element, newTagName)
 */

/**
 * According to the HTML specification
 * (https://html.spec.whatwg.org/#boolean-attribute)
 *
 *  "The presence of a boolean attribute on an element represents the “true” value,
 *   and the absence of the attribute represents the “false” value."
 */
const booleanAttributes = [
    "allowfullscreen",
    "allowpaymentrequest",
    "async",
    "autofocus",
    "autoplay",
    "checked",
    "controls",
    "default",
    "disabled",
    "formnovalidate",
    "hidden",
    "ismap",
    "itemscope",
    "loop",
    "multiple",
    "muted",
    "nomodule",
    "novalidate",
    "open",
    "playsinline",
    "readonly",
    "required",
    "reversed",
    "selected",
    "truespeed"
];

/**
 * Create an element with optional attributes object
 *
 * @param {String} tagName
 * @param {Object} attributes
 */
export function createElement(tagName, attributes = {}) {
    const element = document.createElement(tagName);

    Object.keys(attributes).forEach((attribute) => {
        if (booleanAttributes.includes(attribute)) {
            element[attribute] = attributes[attribute];
        } else {
            element.setAttribute(attribute, attributes[attribute]);
        }
    });

    return element;
}

/**
 * Detect if an element overflows its container
 * @param {Element} element
 * @returns
 */
export function detectOverflow(element) {
    return {
        horizontal: element.scrollWidth > element.clientWidth,
        vertical: element.scrollHeight > element.clientHeight
    };
}

/**
 * A list of all the HTML focusable elements
 * @note: May need to be update from time-to-time as/when new HTML elements are
 * created in the official specification
 */
export const focusableElements = (() => {
    return [
        "a[href]",
        "area[href]",
        "audio[controls]",
        "button:not([disabled])",
        "embed",
        "iframe",
        "input:not([disabled]):not([type='hidden'])",
        "object",
        "select:not([disabled])",
        "summary",
        "textarea:not([disabled])",
        "video[controls]",
        "[contenteditable]",
        // @note: `tabindex="-1"` is deliberately left out
        // It is for JS focus only, not part of regular keyboard focusable elements;
        // it should never be included in this list
        "[tabindex^='0']",
        "[tabindex^='1']",
        "[tabindex^='2']",
        "[tabindex^='3']",
        "[tabindex^='4']",
        "[tabindex^='5']",
        "[tabindex^='6']",
        "[tabindex^='7']",
        "[tabindex^='8']",
        "[tabindex^='9']"
    ];
})();

/**
 * Return an element's attributes as an object of key/value pairs
 *
 * @param {Element} element
 */
export function getAttributes(element) {
    const attributes = {};
    if (isElement(element)) {
        for (let i = 0; i < element.attributes.length; i += 1) {
            const { nodeName, nodeValue } = element.attributes[i];

            if (booleanAttributes.includes(nodeName)) {
                attributes[nodeName] = true;
            } else {
                attributes[nodeName] = nodeValue;
            }
        }
    }

    return attributes;
}

/**
 * Get true element position (relative to page)
 * Unaffected by parent elements being alternatively positioned via CSS
 * e.g. items that are `position: fixed/sticky`
 *
 * @param {Element} element
 *
 * @returns {Object}
 */
export function getPosition(element) {
    const position = {
        top: 0,
        left: 0
    };

    if (element.offsetParent) {
        while (element) {
            position.top += element.offsetTop;
            position.left += element.offsetLeft;
            element = element.offsetParent;
        }
    }

    return position;
}

/**
 * Test for true DOM element/node status
 * E.g. Good to check before calling further node-specific methods, which will
 * throw JS errors on non-element items
 *
 * @param {Element} element
 */
export function isElement(element) {
    return element instanceof Element;
}

/**
 * Replace one tag with another
 * Copies over all attributes and content
 *
 * @param   {Element}  element
 * @param   {String}  newTagName  -- Defaults to "button" (most common usage)
 *
 * @return  {Element}  -- The new replacement element
 */
export function swapTag(element, newTagName = "button") {
    if (element.tagName.toUpperCase() === newTagName.toUpperCase()) {
        // No action required, return the original element
        return element;
    }

    const replacement = document.createElement(newTagName);
    // Copy attributes & content over
    element.getAttributeNames().forEach((attr) => {
        replacement.setAttribute(attr, element.getAttribute(attr));
    });
    replacement.innerHTML = `${element.innerHTML}`;

    element.replaceWith(replacement);
    return replacement;
}

export default {
    createElement,
    detectOverflow,
    focusableElements,
    getAttributes,
    getPosition,
    isElement,
    swapTag
};
